import { useState, useEffect } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import AlertCard from '@/components/Dashboard/AlertCard';
import { aiAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Robot, Lightning, ChartLine, Warning, Shield, TrendUp } from '@phosphor-icons/react';

export default function AIAlertsPage() {
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const response = await aiAPI.getAlerts();
      setAlerts(response.data.alerts || []);
      setAnalyses(response.data.analysis_results || []);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAlerts();
  }, []);

  const runAnalysis = async (type) => {
    setAnalyzing(true);
    try {
      const response = await aiAPI.analyze({ analysis_type: type });
      toast.success(`Analysis started: ${response.data.analysis_id}`);
      // Poll for results
      setTimeout(() => {
        loadAlerts();
      }, 5000);
    } catch (error) {
      toast.error('Failed to start analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const analysisTypes = [
    { type: 'anomaly', label: 'Anomaly Detection', icon: Warning, description: 'Detect unusual patterns in transactions' },
    { type: 'fraud', label: 'Fraud Detection', icon: Shield, description: 'Identify potential fraudulent activities' },
    { type: 'cashflow', label: 'Cash Flow Prediction', icon: TrendUp, description: 'Predict future cash flow trends' },
    { type: 'scoring', label: 'Clinic Scoring', icon: ChartLine, description: 'Score clinic performance' },
  ];

  return (
    <div data-testid="ai-alerts-page">
      <PageHeader 
        title="AI Intelligence" 
        subtitle="AI-powered financial insights and alerts"
      />
      
      <div className="page-content">
        {/* Analysis Actions */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {analysisTypes.map((analysis) => {
            const Icon = analysis.icon;
            return (
              <button
                key={analysis.type}
                onClick={() => runAnalysis(analysis.type)}
                disabled={analyzing}
                className="bg-white border border-[#E2E8F0] rounded-lg p-4 text-left hover:border-[#0F4C81] transition-colors card-hover"
                data-testid={`run-${analysis.type}-analysis`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-[#E2E8F0]">
                    <Icon size={20} className="text-[#0F4C81]" />
                  </div>
                  <h4 className="font-medium text-sm font-['Plus_Jakarta_Sans']">{analysis.label}</h4>
                </div>
                <p className="text-xs text-[#64748B]">{analysis.description}</p>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Active Alerts */}
          <div className="col-span-2">
            <div className="bg-white border border-[#E2E8F0] rounded-lg">
              <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Robot size={20} className="text-[#0F4C81]" />
                  <h3 className="font-medium font-['Plus_Jakarta_Sans']">Active Alerts</h3>
                </div>
                <span className="text-xs text-[#64748B]">{alerts.length} alerts</span>
              </div>
              <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <div key={i} className="skeleton h-20 rounded-lg"></div>
                  ))
                ) : alerts.length > 0 ? (
                  alerts.map((alert, index) => (
                    <AlertCard key={index} alert={{ ...alert, type: 'ai' }} />
                  ))
                ) : (
                  <div className="text-center py-8 text-[#64748B]">
                    <Robot size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No active alerts</p>
                    <p className="text-xs mt-1">Run an analysis to generate insights</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Analysis Results */}
          <div className="col-span-1">
            <div className="bg-white border border-[#E2E8F0] rounded-lg">
              <div className="p-4 border-b border-[#E2E8F0]">
                <h3 className="font-medium font-['Plus_Jakarta_Sans']">Recent Analyses</h3>
              </div>
              <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto">
                {analyses.length > 0 ? (
                  analyses.map((analysis, index) => (
                    <div key={index} className="p-3 bg-[#F8FAFC] rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium uppercase text-[#0F4C81]">
                          {analysis.type}
                        </span>
                        <span className={`text-xs ${analysis.status === 'completed' ? 'text-green-600' : 'text-amber-600'}`}>
                          {analysis.status}
                        </span>
                      </div>
                      {analysis.result && (
                        <p className="text-xs text-[#64748B] line-clamp-3">
                          {typeof analysis.result === 'string' ? analysis.result.slice(0, 200) + '...' : 'Analysis complete'}
                        </p>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-[#64748B]">
                    <Lightning size={24} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No recent analyses</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* AI Features Info */}
        <div className="mt-6 p-6 bg-gradient-to-r from-[#0F4C81] to-[#0F4C81] rounded-lg text-white">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white/10 rounded-lg">
              <Robot size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] mb-2">AI-Powered Financial Intelligence</h3>
              <p className="text-sm opacity-80 mb-4">
                Our AI engine continuously monitors your financial data to detect anomalies, 
                predict cash flow trends, identify potential fraud, and score clinic performance.
              </p>
              <div className="grid grid-cols-4 gap-4 text-xs">
                <div>
                  <p className="font-semibold">Anomaly Detection</p>
                  <p className="opacity-70">Flags unusual transactions</p>
                </div>
                <div>
                  <p className="font-semibold">Fraud Detection</p>
                  <p className="opacity-70">Identifies suspicious patterns</p>
                </div>
                <div>
                  <p className="font-semibold">Cash Flow Prediction</p>
                  <p className="opacity-70">30-day forecasting</p>
                </div>
                <div>
                  <p className="font-semibold">Performance Scoring</p>
                  <p className="opacity-70">Clinic rankings & insights</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
